/**
 * CSS Injector Service
 * Injects CSS custom properties at runtime to override SCSS theme variables
 */

import {
  isValidHexColor,
  sanitizeColor,
  lightenColor,
  darkenColor,
  generateColorPalette
} from '../utils/color-utils';

// Style element ID
const STYLE_ELEMENT_ID = 'neurograph-theme-override';

// CSS variable prefix
const CSS_VAR_PREFIX = '--axon-color';

/**
 * Map API branding colors to axon color system
 * API provides: primary_color, secondary_color, tertiary_color, color1-5
 * Axon uses: $axon-color-1 through $axon-color-5
 *
 * Mapping strategy:
 * - axon-color-1 (warning/dark): API color1 or fallback
 * - axon-color-2 (info/medium): API color2 or fallback
 * - axon-color-3 (secondary/accent): API secondary_color
 * - axon-color-4 (primary/brand): API primary_color
 * - axon-color-5 (default/light): API color3 or fallback
 */
const COLOR_MAPPING = {
  'axon-color-1': 'color1',      // Warning/Dark
  'axon-color-2': 'color2',      // Info/Medium
  'axon-color-3': 'secondary_color', // Secondary/Accent (green default)
  'axon-color-4': 'primary_color',   // Primary/Brand (purple default)
  'axon-color-5': 'color3'       // Default/Light
};

/**
 * CSSInjector class for runtime theme variable injection
 */
class CSSInjector {
  constructor() {
    this.styleElement = null;
    this.currentTheme = null;
  }

  /**
   * Map API branding to CSS variable object
   *
   * @param {object} branding - The branding data from API
   * @returns {object} CSS variables mapping
   */
  mapBrandingToVariables(branding) {
    const variables = {};

    // Map each axon color to corresponding API field
    Object.entries(COLOR_MAPPING).forEach(([axonColor, apiField]) => {
      const colorValue = branding[apiField];
      if (colorValue && isValidHexColor(colorValue)) {
        const sanitized = sanitizeColor(colorValue);
        if (sanitized) {
          variables[`${CSS_VAR_PREFIX}-${axonColor.split('-')[2]}`] = sanitized;

          // Generate lighter/darker variants
          const palette = generateColorPalette(sanitized);
          variables[`${CSS_VAR_PREFIX}-${axonColor.split('-')[2]}-light`] = palette.light;
          variables[`${CSS_VAR_PREFIX}-${axonColor.split('-')[2]}-lighter`] = palette.lighter;
          variables[`${CSS_VAR_PREFIX}-${axonColor.split('-')[2]}-dark`] = palette.dark;
          variables[`${CSS_VAR_PREFIX}-${axonColor.split('-')[2]}-darker`] = palette.darker;
        }
      }
    });

    // Handle additional colors if provided
    if (branding.tertiary_color && isValidHexColor(branding.tertiary_color)) {
      const sanitized = sanitizeColor(branding.tertiary_color);
      if (sanitized) {
        variables['--axon-tertiary-color'] = sanitized;
      }
    }

    if (branding.color4 && isValidHexColor(branding.color4)) {
      const sanitized = sanitizeColor(branding.color4);
      if (sanitized) {
        variables['--axon-accent-color'] = sanitized;
      }
    }

    if (branding.color5 && isValidHexColor(branding.color5)) {
      const sanitized = sanitizeColor(branding.color5);
      if (sanitized) {
        variables['--axon-highlight-color'] = sanitized;
      }
    }

    return variables;
  }

  /**
   * Generate CSS text from variables object
   *
   * @param {object} variables - CSS variables object
   * @returns {string} CSS text
   */
  generateCSSText(variables) {
    const cssVars = Object.entries(variables)
      .map(([name, value]) => `  ${name}: ${value};`)
      .join('\n');

    return `:root {\n${cssVars}\n}`;
  }

  /**
   * Inject theme CSS variables into DOM
   *
   * @param {object} branding - The branding data
   */
  injectTheme(branding) {
    if (!branding) {
      console.warn('No branding data provided for injection');
      return;
    }

    // Map branding to CSS variables
    const variables = this.mapBrandingToVariables(branding);

    if (Object.keys(variables).length === 0) {
      console.warn('No valid colors found in branding data');
      return;
    }

    // Generate CSS text
    const cssText = this.generateCSSText(variables);

    // Inject into DOM
    this.injectCSSText(cssText);

    // Store current theme
    this.currentTheme = branding;

    console.debug('Theme injected successfully:', Object.keys(variables));
  }

  /**
   * Inject CSS text into DOM
   *
   * @param {string} cssText - The CSS text to inject
   */
  injectCSSText(cssText) {
    // Get or create style element
    if (!this.styleElement) {
      this.styleElement = document.getElementById(STYLE_ELEMENT_ID);

      if (!this.styleElement) {
        this.styleElement = document.createElement('style');
        this.styleElement.id = STYLE_ELEMENT_ID;
        this.styleElement.type = 'text/css';
        document.head.appendChild(this.styleElement);
      }
    }

    // Update CSS text
    this.styleElement.textContent = cssText;
  }

  /**
   * Update specific CSS variable
   *
   * @param {string} name - Variable name (with or without --)
   * @param {string} value - Variable value
   */
  updateVariable(name, value) {
    const varName = name.startsWith('--') ? name : `--${name}`;
    document.documentElement.style.setProperty(varName, value);
  }

  /**
   * Update multiple CSS variables
   *
   * @param {object} variables - Variables object
   */
  updateVariables(variables) {
    Object.entries(variables).forEach(([name, value]) => {
      this.updateVariable(name, value);
    });
  }

  /**
   * Apply theme with smooth transition
   *
   * @param {object} branding - The branding data
   * @param {number} duration - Transition duration in ms (default: 300)
   */
  applyThemeWithTransition(branding, duration = 300) {
    // Add transition class
    document.documentElement.classList.add('theme-transitioning');

    // Inject theme
    this.injectTheme(branding);

    // Remove transition class after duration
    setTimeout(() => {
      document.documentElement.classList.remove('theme-transitioning');
    }, duration);
  }

  /**
   * Remove injected theme and reset to defaults
   */
  resetTheme() {
    if (this.styleElement) {
      this.styleElement.remove();
      this.styleElement = null;
    }

    this.currentTheme = null;

    console.debug('Theme reset to defaults');
  }

  /**
   * Get currently applied theme
   *
   * @returns {object|null} Current theme branding or null
   */
  getCurrentTheme() {
    return this.currentTheme;
  }

  /**
   * Get current CSS variables from DOM
   *
   * @returns {object} Current CSS variables
   */
  getCurrentVariables() {
    const styles = getComputedStyle(document.documentElement);
    const variables = {};

    // Get all custom properties starting with --axon-color
    for (let i = 0; i < styles.length; i++) {
      const prop = styles[i];
      if (prop.startsWith('--axon-color')) {
        variables[prop] = styles.getPropertyValue(prop).trim();
      }
    }

    return variables;
  }

  /**
   * Check if theme is currently applied
   *
   * @returns {boolean} True if theme is applied
   */
  hasThemeApplied() {
    return this.currentTheme !== null;
  }

  /**
   * Inject transition CSS for smooth theme changes
   */
  injectTransitionCSS() {
    const transitionCSS = `
      .theme-transitioning,
      .theme-transitioning * {
        transition: background-color 0.3s ease,
                    color 0.3s ease,
                    border-color 0.3s ease,
                    box-shadow 0.3s ease !important;
      }
    `;

    const transitionStyle = document.createElement('style');
    transitionStyle.id = 'neurograph-theme-transition';
    transitionStyle.textContent = transitionCSS;
    document.head.appendChild(transitionStyle);
  }

  /**
   * Initialize injector (call once on app startup)
   */
  initialize() {
    this.injectTransitionCSS();
  }
}

// Export singleton instance
export default new CSSInjector();

// Export class for custom instances
export { CSSInjector };
